import React, { useState } from "react";
import { FiCalendar, FiLock, FiMapPin, FiUser } from "react-icons/fi";
import { updateCollectorItem } from "../../services/collectorApi";
import { parsePurchasePrice } from "../../helpers/collectorUpload";

/**
 * "Add later" (S4): after a quick add the piece is often just a photo. This card asks one missing
 * detail at a time. Each answer saves on its own (a PATCH with only that field); "Skip for now"
 * moves on without saving and is remembered for this visit; "I don't know" is remembered on this
 * phone so the card never nags about it again. When nothing is missing the card is not shown.
 */

export type LaterQuestion = "makerName" | "acquiredPlace" | "acquiredDate" | "purchasePrice";

export const LATER_QUESTIONS: LaterQuestion[] = ["makerName", "acquiredPlace", "acquiredDate", "purchasePrice"];

type LaterItem = {
  makerName?: string | null;
  acquiredPlace?: string | null;
  acquiredDate?: string | null;
  purchasePrice?: number | string | null;
};

const isFilled = (item: LaterItem, key: LaterQuestion) => {
  const value = item[key];
  return value != null && String(value).trim() !== "";
};

export const answeredCount = (item: LaterItem) => LATER_QUESTIONS.filter((key) => isFilled(item, key)).length;

/** The questions still worth asking, in order, leaving out skipped and "don't know" ones. */
export function openQuestions(item: LaterItem, setAside: Set<string>): LaterQuestion[] {
  return LATER_QUESTIONS.filter((key) => !isFilled(item, key) && !setAside.has(key));
}

/**
 * "When did you get it?" chips. acquiredDate is a free string (the full edit form writes YYYY-MM-DD),
 * so a chip stores words that stay true later: a year, not "this year".
 */
export function whenChips(now: Date = new Date()): { label: string; value: string }[] {
  const year = now.getFullYear();
  return [
    { label: "This year", value: String(year) },
    { label: "A few years ago", value: `A few years before ${year}` },
    { label: "Family heirloom", value: "Family heirloom" },
  ];
}

export const PLACE_CHIPS = ["A shop", "An exhibition", "Online", "A gift", "From family"];

const storageKey = (itemKey: string) => `hkp-mc-later:${itemKey}`;

function readSet(store: () => Storage, itemKey: string): Set<string> {
  try {
    const raw = store().getItem(storageKey(itemKey));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeSet(store: () => Storage, itemKey: string, values: Set<string>) {
  try {
    store().setItem(storageKey(itemKey), JSON.stringify(Array.from(values)));
  } catch {
    // Private mode or storage blocked: the card just asks again next time.
  }
}

const session = () => window.sessionStorage;
const local = () => window.localStorage;

const isYear = (text: string, now: Date) => {
  if (!/^\d{4}$/.test(text)) return false;
  const year = Number(text);
  return year >= 1800 && year <= now.getFullYear();
};

type Props = {
  itemKey: string;
  item: LaterItem;
  onSaved: (updated: Record<string, unknown>) => void;
};

const McAddLaterCard: React.FC<Props> = ({ itemKey, item, onSaved }) => {
  const [skipped, setSkipped] = useState<Set<string>>(() => readSet(session, itemKey));
  const [unknown, setUnknown] = useState<Set<string>>(() => readSet(local, itemKey));
  const [drafts, setDrafts] = useState<Record<LaterQuestion, string>>({
    makerName: "",
    acquiredPlace: "",
    acquiredDate: "",
    purchasePrice: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [failedBody, setFailedBody] = useState<Record<string, unknown> | null>(null);

  const setAside = new Set<string>([...Array.from(skipped), ...Array.from(unknown)]);
  const open = openQuestions(item, setAside);
  const question = open[0];
  if (!question) return null;

  const done = answeredCount(item);
  const draft = drafts[question];
  const setDraft = (value: string) => {
    setDrafts((prev) => ({ ...prev, [question]: value }));
    setError("");
  };

  const save = async (body: Record<string, unknown>) => {
    setSaving(true);
    setError("");
    setFailedBody(null);
    try {
      const res = await updateCollectorItem(itemKey, body);
      const updated = res?.data?.data;
      onSaved(updated && typeof updated === "object" ? updated : { ...item, ...body });
    } catch {
      // Keep what they typed; one tap tries the same answer again.
      setError("Could not save. Check your internet and try again.");
      setFailedBody(body);
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    const next = new Set(skipped);
    next.add(question);
    setSkipped(next);
    writeSet(session, itemKey, next);
    setError("");
    setFailedBody(null);
  };

  const dontKnow = () => {
    const next = new Set(unknown);
    next.add(question);
    setUnknown(next);
    writeSet(local, itemKey, next);
    setError("");
    setFailedBody(null);
  };

  const submitTyped = (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) {
      setError(question === "purchasePrice" ? "Type the amount, or tap Don't remember." : "Type an answer, or tap Skip for now.");
      return;
    }
    if (question === "purchasePrice") {
      let amount: number | null;
      try {
        amount = parsePurchasePrice(text);
      } catch {
        setError("Type the amount in numbers, like 18500.");
        return;
      }
      save({ purchasePrice: amount, purchaseCurrency: "INR" });
      return;
    }
    if (question === "acquiredDate" && !isYear(text, new Date())) {
      setError("Type the year, like 1994.");
      return;
    }
    save({ [question]: text });
  };

  const chip = (label: string, value: string) => (
    <button
      key={label}
      type="button"
      className="mc-later-chip"
      disabled={saving}
      onClick={() => save({ [question]: value })}
    >
      {label}
    </button>
  );

  const headings: Record<LaterQuestion, { icon: React.ReactNode; title: string }> = {
    makerName: { icon: <FiUser />, title: "Who made it?" },
    acquiredPlace: { icon: <FiMapPin />, title: "Where did you get it?" },
    acquiredDate: { icon: <FiCalendar />, title: "When did you get it?" },
    purchasePrice: { icon: "₹", title: "What did you pay?" },
  };
  const heading = headings[question];
  const inputId = `mc-later-${question}`;

  return (
    <section className="mc-later" aria-labelledby="mc-later-title">
      <div className="mc-later-progress">
        <div className="mc-later-progress-row">
          <span>Details so far</span>
          <span>{done} of {LATER_QUESTIONS.length}</span>
        </div>
        <div className="mc-later-bar" aria-hidden="true">
          <span style={{ width: `${(done / LATER_QUESTIONS.length) * 100}%` }} />
        </div>
      </div>

      <form className="mc-later-card" onSubmit={submitTyped} noValidate>
        <h2 id="mc-later-title" className="mc-later-title">
          <span className="mc-later-icon" aria-hidden="true">{heading.icon}</span>
          {heading.title}
        </h2>

        {question === "purchasePrice" && (
          <p className="mc-later-private">
            <FiLock aria-hidden="true" />
            Only you see this. Never on a placard or QR page.
          </p>
        )}

        {question === "acquiredPlace" && (
          <div className="mc-later-chips">{PLACE_CHIPS.map((label) => chip(label, label))}</div>
        )}
        {question === "acquiredDate" && (
          <div className="mc-later-chips">{whenChips().map(({ label, value }) => chip(label, value))}</div>
        )}

        <label htmlFor={inputId} className={question === "makerName" ? "visually-hidden" : "mc-later-label"}>
          {question === "makerName" && "Who made it"}
          {question === "acquiredPlace" && "Or type the town or shop"}
          {question === "acquiredDate" && "Or type the year"}
          {question === "purchasePrice" && "Amount in rupees"}
        </label>
        <div className={question === "purchasePrice" ? "mc-later-input mc-later-money" : "mc-later-input"}>
          {question === "purchasePrice" && <span aria-hidden="true">₹</span>}
          <input
            id={inputId}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            autoComplete="off"
            inputMode={question === "purchasePrice" || question === "acquiredDate" ? "numeric" : "text"}
            maxLength={question === "acquiredDate" ? 4 : 120}
            placeholder={
              question === "makerName" ? "A weaver, a shop or a brand"
                : question === "acquiredPlace" ? "Banaras"
                  : question === "acquiredDate" ? "1994"
                    : "18500"
            }
          />
        </div>

        {question === "makerName" && (
          <div className="mc-later-chips">
            <button type="button" className="mc-later-chip" disabled={saving} onClick={dontKnow}>
              I don't know
            </button>
          </div>
        )}

        {error && (
          <div className="mc-later-error" role="alert">
            <span>{error}</span>
            {failedBody && (
              <button type="button" className="mc-btn-ghost" disabled={saving} onClick={() => save(failedBody)}>
                Try again
              </button>
            )}
          </div>
        )}

        <div className="mc-later-actions">
          <button
            type="button"
            className="mc-later-skip"
            disabled={saving}
            onClick={question === "purchasePrice" ? dontKnow : skip}
          >
            {question === "purchasePrice" ? "Don't remember" : "Skip for now"}
          </button>
          <button type="submit" className="mc-btn-primary mc-later-save" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
      <p className="mc-later-note">Answers save as you go.</p>
    </section>
  );
};

export default McAddLaterCard;
